import { env } from '@/config/env'
import { apiRoutes } from '@/config/routes'
import { CROSSCHAIN_PROVIDER } from '@/constants/crosschain'
import type { CctpContextArgs, CctpSdkContext, CctpTransactionsArgs } from '@/types/sdk/experimental/cctp/cctpTypes'
import type {
  IntentExecuteArgs,
  IntentGetQuoteArgs,
  IntentSdkContext,
  IntentStatusArgs,
} from '@/types/sdk/experimental/crosschain/crosschainIntentTypes'
import type { Vault } from '@/types/vault'
import { getCctpContext } from '@/utils/sdk/experimental/cctp/getCctpContext'
import { getCctpMoneyInTransit } from '@/utils/sdk/experimental/cctp/getCctpMoneyInTransit'
import { getCctpTransactions } from '@/utils/sdk/experimental/cctp/getCctpTransactions'
import { executeIntent } from '@/utils/sdk/experimental/crosschain/executeIntent'
import { getIntentQuote } from '@/utils/sdk/experimental/crosschain/getIntentQuote'
import { getIntentStatus } from '@/utils/sdk/experimental/crosschain/getIntentStatus'
import type { Network } from '@vaultsfyi/common/types'
import type { Address } from 'viem'

export interface BorrowMarketsQueryParams {
  network?: Network
  page?: number
  perPage?: number
  minTvl?: number
}

interface FixedTermMarketsQueryParams {
  perPage?: number
  allowedNetworks?: string[]
  allowedAssets?: string[]
  minTvl?: number
  search?: string
  sortBy?: 'tvl' | 'fixedApy' | 'liquidity' | 'expiry'
  sortOrder?: 'asc' | 'desc'
}

export interface FixedTermMarketPathParams {
  network: Network
  vaultId: Address
}

interface FixedTermAsset {
  address: Address
  assetCaip: string
  name?: string
  symbol?: string
  decimals?: number
  assetLogo?: string
  assetPriceInUsd?: string
  assetGroup?: string
}

interface FixedTermProtocol {
  name: string
  displayName?: string
  product?: string
  version?: string
  protocolUrl?: string
  description?: string
  protocolLogo?: string
}

interface FixedTermUsdAmount {
  usd: string
}

export interface FixedTermMarket {
  vaultId: Address
  address: Address
  name: string
  network: {
    name: Network
    chainId: number
    networkCaip: string
  }
  protocol: FixedTermProtocol
  underlyingProtocol: FixedTermProtocol
  pt: FixedTermAsset
  underlyingAsset: FixedTermAsset
  accountingAsset: FixedTermAsset
  fixedApy: number
  expiry: number
  tvl: FixedTermUsdAmount
  liquidity: FixedTermUsdAmount
}

export interface BorrowMarketPathParams {
  network: Network
  marketId: string
}

interface BorrowMarketHistoricalQueryParams extends BorrowMarketPathParams {
  assetId: string
  page?: number
  perPage?: number
  granularity?: '1hour' | '1day' | '1week'
  fromTimestamp?: number
  toTimestamp?: number
}

interface BorrowMarketPortfolioAssetPathParams extends BorrowMarketPathParams {
  userAddress: Address
  assetId: string
}

interface BorrowMarketEventsQueryParams {
  page?: number
  perPage?: number
}

interface AdvancedAnalyticsQueryParams {
  network: Vault['network']['name']
  vaultId: Vault['vaultId']
  select?: {
    composition?: true
  }
}

export interface AdvancedAnalyticsCompositionItem {
  name: string
  allocation?: number
  network: Vault['network']
  vaultId?: Vault['vaultId']
  lpToken: {
    address?: Address
    tokenCaip?: string
    name?: string
    symbol?: string
    decimals?: number
    balanceNative: string
    balanceUsd?: string
  }
  asset?: Vault['asset'] & {
    positionValueInAsset: string
    positionValueInUsd?: string
  }
}

interface AdvancedAnalytics {
  vaultId: Vault['vaultId']
  network: Vault['network']['name']
  composition?: AdvancedAnalyticsCompositionItem[]
}

interface BorrowMarketAmount {
  native: string
  usd?: string
}

interface BorrowMarketAsset {
  address: Address
  assetCaip: string
  name: string
  symbol: string
  decimals: number
  assetLogo?: string
  assetPriceInUsd?: string
  assetGroup: string
  oraclePriceUsd?: string
}

interface BorrowMarketRiskParameters {
  maxLtv: number
  liquidationThreshold: number
  liquidationPenalty?: number
}

interface BorrowMarketInterestRateModel {
  model: 'single-kink'
  baseRate: number
  optimalUtilization: number
  slope1: number
  slope2: number
}

export interface BorrowMarketAssetData {
  marketAssetId: string
  asset: BorrowMarketAsset
  isCollateralEnabled: boolean
  supportsHistoricalData: boolean
  supplyRate?: number
  borrowRate?: number
  utilizationRate?: number
  totalSupplied?: BorrowMarketAmount
  totalBorrowed?: BorrowMarketAmount
  availableLiquidity?: BorrowMarketAmount
  availableToBorrow?: BorrowMarketAmount
  assetConfig: {
    isBorrowEnabled: boolean
    borrowInterestFeeRate?: number
    borrowCap?: string
    supplyCap?: string
  }
  interestRateModel?: BorrowMarketInterestRateModel
  defaultRiskParameters?: BorrowMarketRiskParameters
  pairwiseRiskParameters?: Record<string, BorrowMarketRiskParameters>
}

export interface BorrowMarket {
  marketId: string
  name: string
  network: {
    name: Network
    chainId: number
    networkCaip: string
  }
  protocol: {
    name: string
    displayName?: string
    product?: string
    version?: string
    protocolLogo?: string
  }
  marketRootAddress: Address
  assetsData: BorrowMarketAssetData[]
}

export interface BorrowMarketPair {
  collateralAssetAddress: Address
  collateralMarketAssetId: string
  borrowAssetAddress: Address
  borrowMarketAssetId: string
  collateralEnabled: boolean
  borrowEnabled: boolean
  riskParameters: BorrowMarketRiskParameters
  collateralToBorrowAssetOraclePrice?: string
}

export interface BorrowMarketDetail extends BorrowMarket {
  pairs: BorrowMarketPair[]
}

interface BorrowMarketsResponse {
  nextPage?: number
  itemsOnPage: number
  data: BorrowMarket[]
}

interface FixedTermMarketsResponse {
  nextPage?: number
  itemsOnPage: number
  data: FixedTermMarket[]
}

interface BorrowMarketResponse {
  market: BorrowMarketDetail
}

export interface BorrowMarketHistoricalDataPoint {
  timestamp: number
  blockNumber: string
  supplyRate: number
  borrowRate: number
  utilizationRate: number
  totalSupplied: BorrowMarketAmount
  totalBorrowed: BorrowMarketAmount
  supplyIndex: number
  borrowIndex: number
}

interface BorrowMarketHistoricalResponse {
  nextPage?: number
  itemsOnPage: number
  data: BorrowMarketHistoricalDataPoint[]
}

export interface BorrowPositionAsset {
  marketAssetId: string
  asset: BorrowMarketAsset
  collateralEnabled: boolean
  balanceNative: string
  balanceUsd?: string
  suppliedNative: string
  suppliedUsd?: string
  borrowedNative: string
  borrowedUsd?: string
}

export interface BorrowMarketPosition {
  marketId: string
  name: string
  network: BorrowMarket['network']
  protocol: BorrowMarket['protocol']
  userAddress: Address
  ltv?: number
  healthFactor?: number
  assets: BorrowPositionAsset[]
}

export interface BorrowMarketAssetPosition extends Omit<BorrowMarketPosition, 'assets'> {
  asset: BorrowPositionAsset
}

export interface BorrowMarketEvent {
  timestamp: number
  blockNumber: string
  eventType: 'supply' | 'withdraw' | 'borrow' | 'repay'
  assetAmountNative: string
  transactionHash: string
  logIndex: number
}

interface BorrowPositionsResponse {
  data: BorrowMarketPosition[]
  errors: {
    unsupportedNetworks: string[]
    unsupportedAssets: string[]
  }
}

interface BorrowMarketEventsResponse {
  nextPage?: number
  itemsOnPage: number
  data: BorrowMarketEvent[]
}

interface BorrowPositionsQueryParams {
  minUsdAssetValueThreshold?: number
}

const DEFAULT_FIXED_TERM_MARKETS_PER_PAGE = 5_000
const MAX_FIXED_TERM_MARKET_PAGES = 100

class InternalAppSDK {
  private apiBaseUrl: string
  private provider: string

  constructor({ apiBaseUrl, provider }: { apiBaseUrl: string; provider: string }) {
    this.apiBaseUrl = apiBaseUrl
    this.provider = provider
  }

  private get experimentalTransactionsBase() {
    return `${this.apiBaseUrl}/experimental/transactions`
  }

  private get intentCtx(): IntentSdkContext {
    return { base: this.experimentalTransactionsBase, provider: this.provider }
  }

  private get cctpCtx(): CctpSdkContext {
    return { base: this.experimentalTransactionsBase }
  }

  private async getJson<T>(path: string, fallbackError: string): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      headers:
        typeof window === 'undefined'
          ? {
              'Content-Type': 'application/json',
              'x-api-key': env.VAULTS_API_KEY,
            }
          : undefined,
    })

    if (!response.ok) {
      throw new Error(fallbackError)
    }

    return response.json() as Promise<T>
  }

  getBorrowMarkets({ network, page = 0, perPage, minTvl }: BorrowMarketsQueryParams = {}) {
    const params = new URLSearchParams({ page: String(page) })
    if (perPage !== undefined) params.set('perPage', String(perPage))
    if (minTvl !== undefined) params.set('minTvl', String(minTvl))

    const endpoint = network ? `/beta/borrow/markets/${network}` : '/beta/borrow/markets'

    return this.getJson<BorrowMarketsResponse>(`${endpoint}?${params}`, 'Failed to fetch borrow markets')
  }

  async getFixedTermMarkets({
    perPage = DEFAULT_FIXED_TERM_MARKETS_PER_PAGE,
    allowedNetworks,
    allowedAssets,
    minTvl,
    search,
    sortBy,
    sortOrder,
  }: FixedTermMarketsQueryParams = {}) {
    const markets: FixedTermMarket[] = []
    const fetchedPages = new Set<number>()
    let page: number | undefined = 0

    while (page !== undefined) {
      if (fetchedPages.size >= MAX_FIXED_TERM_MARKET_PAGES) {
        throw new Error('Failed to fetch fixed-term markets: maximum page count exceeded')
      }
      if (fetchedPages.has(page)) {
        throw new Error('Failed to fetch fixed-term markets: repeated pagination cursor')
      }

      fetchedPages.add(page)

      const params: URLSearchParams = new URLSearchParams({ page: String(page), perPage: String(perPage) })
      for (const network of allowedNetworks ?? []) params.append('allowedNetworks', network)
      for (const asset of allowedAssets ?? []) params.append('allowedAssets', asset)
      if (minTvl !== undefined) params.set('minTvl', String(minTvl))
      if (search !== undefined) params.set('search', search)
      if (sortBy !== undefined) params.set('sortBy', sortBy)
      if (sortOrder !== undefined) params.set('sortOrder', sortOrder)

      const response: FixedTermMarketsResponse = await this.getJson<FixedTermMarketsResponse>(
        `/beta/fixed-term/?${params}`,
        'Failed to fetch fixed-term markets'
      )
      if (!Array.isArray(response.data)) {
        throw new Error('Failed to fetch fixed-term markets: invalid response data')
      }

      markets.push(...response.data)
      if (response.nextPage !== undefined && (!Number.isInteger(response.nextPage) || response.nextPage <= page)) {
        throw new Error('Failed to fetch fixed-term markets: non-advancing pagination cursor')
      }

      page = response.nextPage
    }

    return markets
  }

  getFixedTermMarket({ network, vaultId }: FixedTermMarketPathParams) {
    const encodedNetwork = encodeURIComponent(network)
    const encodedVaultId = encodeURIComponent(vaultId)

    return this.getJson<FixedTermMarket>(
      `/beta/fixed-term/detailed/${encodedNetwork}/${encodedVaultId}`,
      'Failed to fetch fixed-term market'
    )
  }

  getBorrowMarket({ network, marketId }: BorrowMarketPathParams) {
    const encodedNetwork = encodeURIComponent(network)
    const encodedMarketId = encodeURIComponent(marketId)

    return this.getJson<BorrowMarketResponse>(
      `/beta/borrow/markets/${encodedNetwork}/${encodedMarketId}`,
      'Failed to fetch borrow market'
    )
  }

  getBorrowMarketHistoricalData({
    network,
    marketId,
    assetId,
    page = 0,
    perPage,
    granularity,
    fromTimestamp,
    toTimestamp,
  }: BorrowMarketHistoricalQueryParams) {
    const encodedNetwork = encodeURIComponent(network)
    const encodedMarketId = encodeURIComponent(marketId)
    const encodedAssetId = encodeURIComponent(assetId)
    const params = new URLSearchParams({ page: String(page) })
    if (perPage !== undefined) params.set('perPage', String(perPage))
    if (granularity !== undefined) params.set('granularity', granularity)
    if (fromTimestamp !== undefined) params.set('fromTimestamp', String(fromTimestamp))
    if (toTimestamp !== undefined) params.set('toTimestamp', String(toTimestamp))

    return this.getJson<BorrowMarketHistoricalResponse>(
      `/beta/borrow/markets/historical/${encodedNetwork}/${encodedMarketId}/${encodedAssetId}?${params}`,
      'Failed to fetch borrow market historical data'
    )
  }

  async getAdvancedAnalytics({ network, vaultId, select }: AdvancedAnalyticsQueryParams) {
    const encodedNetwork = encodeURIComponent(network)
    const encodedVaultId = encodeURIComponent(vaultId)
    const response = await fetch(`${this.apiBaseUrl}/beta/advanced-analytics/${encodedNetwork}/${encodedVaultId}`, {
      method: 'POST',
      headers:
        typeof window === 'undefined'
          ? {
              'Content-Type': 'application/json',
              'x-api-key': env.VAULTS_API_KEY,
            }
          : {
              'Content-Type': 'application/json',
            },
      body: JSON.stringify({ select }),
    })

    if (!response.ok) {
      throw new Error('Failed to fetch advanced analytics')
    }

    return response.json() as Promise<AdvancedAnalytics>
  }

  getBorrowPositions(userAddress: Address, { minUsdAssetValueThreshold }: BorrowPositionsQueryParams = {}) {
    const params = new URLSearchParams()
    if (minUsdAssetValueThreshold !== undefined) {
      params.set('minUsdAssetValueThreshold', String(minUsdAssetValueThreshold))
    }

    const queryString = params.size > 0 ? `?${params}` : ''
    return this.getJson<BorrowPositionsResponse>(
      `/beta/borrow/portfolio/positions/${userAddress}${queryString}`,
      'Failed to fetch borrow positions'
    )
  }

  getBorrowMarketAssetPosition({ userAddress, network, marketId, assetId }: BorrowMarketPortfolioAssetPathParams) {
    const encodedUserAddress = encodeURIComponent(userAddress)
    const encodedNetwork = encodeURIComponent(network)
    const encodedMarketId = encodeURIComponent(marketId)
    const encodedAssetId = encodeURIComponent(assetId)

    return this.getJson<BorrowMarketAssetPosition>(
      `/beta/borrow/portfolio/positions/${encodedUserAddress}/${encodedNetwork}/${encodedMarketId}/${encodedAssetId}`,
      'Failed to fetch borrow market asset position'
    )
  }

  getBorrowMarketEvents({
    userAddress,
    network,
    marketId,
    assetId,
    page = 0,
    perPage,
  }: BorrowMarketPortfolioAssetPathParams & BorrowMarketEventsQueryParams) {
    const encodedUserAddress = encodeURIComponent(userAddress)
    const encodedNetwork = encodeURIComponent(network)
    const encodedMarketId = encodeURIComponent(marketId)
    const encodedAssetId = encodeURIComponent(assetId)
    const params = new URLSearchParams({ page: String(page) })
    if (perPage !== undefined) params.set('perPage', String(perPage))

    return this.getJson<BorrowMarketEventsResponse>(
      `/beta/borrow/portfolio/events/${encodedUserAddress}/${encodedNetwork}/${encodedMarketId}/${encodedAssetId}?${params}`,
      'Failed to fetch borrow market events'
    )
  }

  getIntentQuote(args: IntentGetQuoteArgs) {
    return getIntentQuote(this.intentCtx, args)
  }

  executeIntent(args: IntentExecuteArgs) {
    return executeIntent(this.intentCtx, args)
  }

  getIntentStatus(args: IntentStatusArgs) {
    return getIntentStatus(this.intentCtx, args)
  }

  getCctpContext(args: CctpContextArgs) {
    return getCctpContext(this.cctpCtx, args)
  }

  getCctpMoneyInTransit(userAddress: Address) {
    return getCctpMoneyInTransit(this.cctpCtx, userAddress)
  }

  getCctpTransactions(args: CctpTransactionsArgs) {
    return getCctpTransactions(this.cctpCtx, args)
  }
}

export function getInternalAppSdk() {
  const apiBaseUrl = typeof window === 'undefined' ? env.VAULTS_API_URL : apiRoutes.apiProxy
  return new InternalAppSDK({ apiBaseUrl, provider: CROSSCHAIN_PROVIDER })
}
